"use client";

import {
  UseMutateFunction,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import {
  WAITLIST_QUERY_KEYS,
  WaitlistSubscription,
  fetchMultipleWaitlistStatus,
  fetchWaitlistStatus,
} from "@punchup/data/client";
import { createWaitlistEntry, removeWaitlistEntry } from "@punchup/data/legacy";

import dayjs from "@punchup/utils/dayjs";
import { logException } from "@punchup/sentry";
import { revalidatePathClient } from "@punchup/utils/vercel";
import { useUser } from "@punchup/data/legacy/hooks";

interface UseWaitlistStatusProps {
  showId: string;
  ownerId: string;
  isVenue: boolean;
  // When provided, the per-show subscription fetch is skipped and this value
  // is used instead. Pass `null` for "known-not-subscribed". Parents that
  // batch-fetch via [[useMultipleWaitlistStatus]] use this to avoid the
  // N-requests-per-page fan-out that triggers /api/v2/user/waitlist rate limits.
  injectedSubscription?: WaitlistSubscription | null;
}

interface JoinWaitlistParams {
  showName: string;
  email?: string;
  phone?: string;
  receiveUpdates?: boolean;
}

interface LeaveWaitlistParams {
  subscriptionId: string;
}

interface UseWaitlistStatusReturn {
  isSubscribed: boolean;
  subscription: WaitlistSubscription | null | undefined;
  isLoading: boolean;
  error: Error | null;
  joinWaitlist: UseMutateFunction<unknown, Error, JoinWaitlistParams, unknown>;
  isJoining: boolean;
  joinError: Error | null;
  leaveWaitlist: UseMutateFunction<
    unknown,
    Error,
    LeaveWaitlistParams,
    unknown
  >;
  isLeaving: boolean;
  leaveError: Error | null;
  subscriptionMethod: "both" | "email" | "phone" | null;
}

export function useWaitlistStatus({
  showId,
  ownerId,
  isVenue,
  injectedSubscription,
}: UseWaitlistStatusProps): UseWaitlistStatusReturn {
  const user = useUser();
  const queryClient = useQueryClient();

  const hasInjectedSubscription = injectedSubscription !== undefined;

  const {
    data: fetchedSubscription,
    isLoading: isFetching,
    error,
  } = useQuery<WaitlistSubscription | null>({
    queryKey: [WAITLIST_QUERY_KEYS.perShow, showId, user?.email, user?.phone],
    queryFn: () =>
      fetchWaitlistStatus({
        showId,
        userEmail: user?.email,
        userPhone: user?.phone,
      }),
    enabled:
      !hasInjectedSubscription && !!(user?.email || user?.phone) && !!showId,
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  });

  const subscription = hasInjectedSubscription
    ? injectedSubscription
    : fetchedSubscription;
  const isLoading = hasInjectedSubscription ? false : isFetching;

  // Mutation to join waitlist
  const joinWaitlistMutation = useMutation({
    mutationFn: async ({
      showName,
      receiveUpdates = true,
    }: {
      showName: string;
      email?: string;
      phone?: string;
      receiveUpdates?: boolean;
    }) => {
      return createWaitlistEntry({
        showName,
        showId,
        ownerId,
        isVenue,
        receiveUpdates,
      });
    },
    onSuccess: async () => {
      // Invalidate and refetch the waitlist status query
      await queryClient.invalidateQueries({
        queryKey: [
          WAITLIST_QUERY_KEYS.perShow,
          showId,
          user?.email,
          user?.phone,
        ],
        refetchType: "active",
      });

      // Parents that batch-fetch via [[useMultipleWaitlistStatus]] must
      // refetch too so injected subscriptions update for the row.
      await queryClient.invalidateQueries({
        queryKey: [WAITLIST_QUERY_KEYS.batched],
        refetchType: "active",
      });

      // Also invalidate any waitlist dashboard queries
      await queryClient.invalidateQueries({
        queryKey: [WAITLIST_QUERY_KEYS.dashboard],
      });

      // Revalidate the server-side dashboard pages
      try {
        await revalidatePathClient(`/dashboard/waitlist`);
        await revalidatePathClient(`/v/dashboard/waitlist`);
      } catch (error) {
        logException(`Failed to revalidate dashboard ${error}`);
      }
    },
  });

  // Mutation to leave waitlist
  const leaveWaitlistMutation = useMutation({
    mutationFn: async ({ subscriptionId }: { subscriptionId: string }) => {
      return removeWaitlistEntry({ subscriptionId });
    },
    onSuccess: async () => {
      // Optimistically set the subscription to null
      queryClient.setQueryData(
        [WAITLIST_QUERY_KEYS.perShow, showId, user?.email, user?.phone],
        null
      );

      // Invalidate and refetch the waitlist status query
      await queryClient.invalidateQueries({
        queryKey: [
          WAITLIST_QUERY_KEYS.perShow,
          showId,
          user?.email,
          user?.phone,
        ],
        refetchType: "active",
      });

      // Parents that batch-fetch via [[useMultipleWaitlistStatus]] must
      // refetch too so injected subscriptions update for the row.
      await queryClient.invalidateQueries({
        queryKey: [WAITLIST_QUERY_KEYS.batched],
        refetchType: "active",
      });

      // Also invalidate any waitlist dashboard queries
      await queryClient.invalidateQueries({
        queryKey: [WAITLIST_QUERY_KEYS.dashboard],
      });

      // Revalidate the server-side dashboard pages
      try {
        await revalidatePathClient(`/dashboard/waitlist`);
        await revalidatePathClient(`/v/dashboard/waitlist`);
      } catch (error) {
        logException(`Failed to revalidate dashboard ${error}`);
      }
    },
  });

  return {
    isSubscribed: !!subscription,
    subscription,
    isLoading,
    error,
    joinWaitlist: joinWaitlistMutation.mutate,
    isJoining: joinWaitlistMutation.isPending,
    joinError: joinWaitlistMutation.error,
    leaveWaitlist: leaveWaitlistMutation.mutate,
    isLeaving: leaveWaitlistMutation.isPending,
    leaveError: leaveWaitlistMutation.error,
    // Helper to know what contact method was used for subscription
    subscriptionMethod: subscription
      ? subscription.email && subscription.phone
        ? "both"
        : subscription.email
          ? "email"
          : "phone"
      : null,
  };
}

export function useMultipleWaitlistStatus(
  shows: Array<{
    id: string;
    ownerId: string;
    isVenue: boolean;
  }>
) {
  const user = useUser();

  const {
    data: subscriptions = [],
    isLoading,
    error,
  } = useQuery({
    queryKey: [
      WAITLIST_QUERY_KEYS.batched,
      shows.map((s) => s.id),
      user?.email,
      user?.phone,
    ],
    queryFn: () =>
      fetchMultipleWaitlistStatus({
        showIds: shows.map((show) => show.id),
        userEmail: user?.email,
        userPhone: user?.phone,
      }),
    enabled: !!(user?.email || user?.phone) && shows.length > 0,
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  });

  // Helper function to check if subscribed to specific show
  const isSubscribedToShow = (showId: string) => {
    return subscriptions.some((sub) => sub.show_id === showId);
  };

  // Get subscription details for a specific show
  const getSubscriptionForShow = (showId: string) => {
    return subscriptions.find((sub) => sub.show_id === showId) || null;
  };

  return {
    subscriptions,
    isLoading,
    error,
    isSubscribedToShow,
    getSubscriptionForShow,
  };
}

export function useWaitlistStatusWithCache({ showId }: UseWaitlistStatusProps) {
  const user = useUser();

  const getCachedStatus = () => {
    if (typeof window === "undefined") return false;

    const cached = localStorage.getItem(`waitlist-${showId}`);
    if (!cached) return false;

    try {
      const { email, phone, timestamp } = JSON.parse(cached);
      const isExpired = dayjs().diff(dayjs(timestamp)) > 5 * 60 * 1000;

      if (isExpired) {
        localStorage.removeItem(`waitlist-${showId}`);
        return false;
      }

      return (
        (user?.email && email === user.email) ||
        (user?.phone && phone === user.phone)
      );
    } catch {
      localStorage.removeItem(`waitlist-${showId}`);
      return false;
    }
  };

  const cachedStatus = getCachedStatus();

  const query = useQuery<WaitlistSubscription | null>({
    queryKey: [WAITLIST_QUERY_KEYS.perShow, showId, user?.email, user?.phone],
    queryFn: async () => {
      const data = await fetchWaitlistStatus({
        showId,
        userEmail: user?.email,
        userPhone: user?.phone,
      });

      if (typeof window !== "undefined") {
        if (data) {
          localStorage.setItem(
            `waitlist-${showId}`,
            JSON.stringify({
              email: user?.email,
              phone: user?.phone,
              timestamp: dayjs().toISOString(),
            })
          );
        } else {
          localStorage.removeItem(`waitlist-${showId}`);
        }
      }

      return data;
    },
    enabled: !!(user?.email || user?.phone) && !!showId,
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
    initialData: cachedStatus ? ({} as WaitlistSubscription) : undefined,
  });

  return {
    isSubscribed: !!query.data || cachedStatus,
    subscription: query.data,
    isLoading: query.isLoading && !cachedStatus,
    error: query.error,
  };
}
